import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as turf from '@turf/turf';

import { Button, Grid, MenuItem, Typography, makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { useSelector, useDispatch } from 'react-redux';
import { MAIN_MAP } from '../../../../../redux/map/Constants';
import { setDrawingFeature, setDrawnFeatures } from '../../../../../redux/map/Actions';

import { PolygonIcon, CircleIcon } from '../../../../Mapbox/Draw/Icons';

import TextField from '../../../../Common/UI/TextField';
import SelectTextField from '../../../../Common/UI/SelectTextField';
import DrawnAreaInfoCard from './DrawnAreaInfoCard';

const KM_TO_METER = 1000;
const MI_TO_METER = 1609.34;

const areaUnits = ['ha', 'km2', 'ac'];

// unit is misleading here as it is for display only purpose
// it doesn't mean the radius stored here is in the differenet unit, it is always 'm'
// TODO - also clicking the polygon removes the custom units field
function updateCircle(geojson, radius, unit = 'm', points = 64) {
    const coordinates = geojson.geometry.coordinates[0];
    const p1 = coordinates[0];
    const p2 = coordinates[32];
    const centre = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];

    const coords = {
        latitude: centre[1],
        longitude: centre[0]
    };
    const km = radius / KM_TO_METER;

    const ret = [];
    const distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
    const distanceY = km / 110.574;

    let theta;
    let x;
    let y;
    for (let i = 0; i < points; i += 1) {
        theta = (i / points) * (2 * Math.PI);
        x = distanceX * Math.cos(theta);
        y = distanceY * Math.sin(theta);

        ret.push([coords.longitude + x, coords.latitude + y]);
    }
    ret.push(ret[0]);
    return {
        ...geojson,
        geometry: {
            ...geojson.geometry,
            coordinates: [ret]
        },
        properties: {
            ...geojson.properties,
            radius: Number(radius)
        }
    };
}

const useStyles = makeStyles(theme => styles(theme));

function DrawnSearchAreaInput({
    featureKey,
    reducerName,
    reducerAreaKey,
    showArea = true,
    disabled = false
}) {
    const classes = useStyles();

    const drawModule = useSelector(state => state[MAIN_MAP].drawModule);
    // const maxArea = useSelector(state => state.user.features[featureKey].maxArea);
    const { defaultAreaUnit } = useSelector(state => state.user.prefs);
    const feature = useSelector(state => state[MAIN_MAP].drawingFeature);
    const geometry = feature && feature.geometry;
    const geojson = geometry && geometry.type === 'Polygon' ? feature : null;
    const [radiusUnit, setRadiusUnit] = useState('m');

    const dispatch = useDispatch();
    const dispatchSetDrawnFeatures = val => dispatch(setDrawnFeatures(val, MAIN_MAP));
    const dispatchSetDrawingFeature = val => dispatch(setDrawingFeature(val, MAIN_MAP));

    function validate() {
        if (!geojson) {
            return null;
        }
        if (turf.area(geojson.geometry) > maxArea) {
            return `Area is too large to perform search, please restrict area to less than ${(
                maxArea * 0.000001
            ).toLocaleString(undefined, { maximumFractionDigits: 0 })} km`;
        }
        return null;
    }

    // Can make a shape so small that you can lose it and can't reclick it? or shapes below a certain size may automatically delete themselves?
    function handleRadiusValueChange(e) {
        let value = Math.max(Number(e.target.value), 0.01);
        switch (radiusUnit) {
            case 'km':
                value = value * KM_TO_METER;
                break;
            case 'mi':
                value = value * MI_TO_METER;
                break;
            default:
                break;
        }

        const newGeoJson = updateCircle(geojson, value, radiusUnit);
        dispatchSetDrawingFeature(newGeoJson);
        let features = drawModule.drawControl.getAll();
        if (features.features.length > 0) {
            let circleIndex;
            features.features.map((feature, index) => {
                if (feature.id === newGeoJson.id) circleIndex = index;
            });
            const circleFeature = features.features?.[circleIndex];
            // Update the radius property of the circle feature
            circleFeature.properties.radius = newGeoJson.properties?.radius;
            circleFeature.geometry.coordinates = newGeoJson.geometry.coordinates;

            features.features[0] = circleFeature;
            // Update the circle on the map
            drawModule.drawControl.set(features);
        }
    }

    function handleRadiusUnitChange(e) {
        setRadiusUnit(e.target.value);
    }

    function radius() {
        const { radius } = geojson.properties;

        let r = 0;
        switch (radiusUnit) {
            case 'km':
                r = radius / KM_TO_METER;
                break;
            case 'mi':
                r = radius / MI_TO_METER;
                break;
            case 'm':
            default:
                r = radius;
                break;
        }
        r = parseFloat(
            r.toLocaleString(undefined, {
                maximumFractionDigits: 2,
                useGrouping: false
            })
        );

        return (
            <Grid container spacing={2} alignContent="center" alignItems="center">
                <Grid item xs={4}>
                    <Typography color="textPrimary">Radius</Typography>
                </Grid>
                <Grid item xs={5}>
                    <TextField
                        type="number"
                        disabled={disabled}
                        error={r <= 0}
                        value={r}
                        onChange={handleRadiusValueChange}
                    />
                </Grid>
                <Grid item xs={3}>
                    <SelectTextField
                        className={classes.radiusUnitSelect}
                        value={radiusUnit}
                        onChange={handleRadiusUnitChange}>
                        {[
                            { name: 'm', id: 1 },
                            { name: 'km', id: 2 },
                            { name: 'mi', id: 3 }
                        ].map(f => (
                            <MenuItem selected={radiusUnit === f.name} key={f.id} value={f.name}>
                                {f.name}
                            </MenuItem>
                        ))}
                    </SelectTextField>
                </Grid>
            </Grid>
        );
    }

    function handleDrawToggleButtonChange(event, drawMode) {
        switch (drawMode) {
            case drawModule.drawControl.modes.DRAW_CIRCLE:
            case drawModule.drawControl.modes.DRAW_POLYGON:
                drawModule.changeMode(drawMode);
                break;
            default:
                drawModule.changeMode(drawModule.drawControl.modes.SIMPLE_SELECT);
                break;
        }
        // Because the inner components of draw control are not state related or managed in Redux,
        // Need to use force update to trigger rerender currently
        // forceUpdate();
    }

    function redrawSearchArea(feature) {
        // TODO - drawcontrol direct delete action should be made to action
        drawModule.getDrawControl().delete(feature.id);
        dispatchSetDrawnFeatures(drawModule.getDrawControl().getAll());
        dispatchSetDrawingFeature(null);
    }

    return (
        <div className={classes.root}>
            {!geojson && (
                <Grid container spacing={2} alignContent="center" alignItems="center">
                    <Grid item xs={6}>
                        <Button
                            classes={{
                                root:
                                    drawModule &&
                                    drawModule.getMode() ===
                                        drawModule.drawControl.modes.DRAW_CIRCLE &&
                                    classes.highlighted
                            }}
                            fullWidth
                            variant="outlined"
                            size="large"
                            color="secondary"
                            onClick={e =>
                                handleDrawToggleButtonChange(
                                    e,
                                    drawModule.drawControl.modes.DRAW_CIRCLE
                                )
                            }>
                            <CircleIcon />
                            Radial Tool
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            classes={{
                                root:
                                    drawModule &&
                                    drawModule.getMode() ===
                                        drawModule.drawControl.modes.DRAW_POLYGON &&
                                    classes.highlighted
                            }}
                            fullWidth
                            variant="outlined"
                            size="large"
                            color="secondary"
                            onClick={e =>
                                handleDrawToggleButtonChange(
                                    e,
                                    drawModule.drawControl.modes.DRAW_POLYGON
                                )
                            }>
                            <PolygonIcon />
                            Custom Draw Tool
                        </Button>
                    </Grid>
                </Grid>
            )}
            {Boolean(geojson?.properties?.radius) && radius()}
            {geojson && showArea && (
                <DrawnAreaInfoCard
                    value={turf.area(geojson.geometry)}
                    label="Area"
                    defaultAreaUnit={areaUnits.includes(defaultAreaUnit) ? defaultAreaUnit : 'ha'}
                    areaUnits={areaUnits}
                />
            )}
            {geojson && showArea && (
                <Grid container spacing={2} justifyContent="flex-end">
                    <Grid item xs={12} className={classes.redrawSearchArea}>
                        <Typography
                            variant="body2"
                            className={classes.redrawSearchAreaText}
                            onClick={() => redrawSearchArea(geojson)}>
                            Redraw Search Area
                        </Typography>
                    </Grid>
                </Grid>
            )}
        </div>
    );
}

const styles = theme => ({
    root: {
        '&> *:not(last-child)': {
            marginBottom: theme.constants.verticalMargin - theme.spacing(2) * 0.5 // grid spacing of 2 then halved for only 1 side
        }
    },
    radiusUnitSelect: {
        width: '100%'
    },
    redrawSearchArea: {
        display: 'flex',
        justifyContent: 'flex-end',
        color: '#9ED6DE'
    },
    redrawSearchAreaText: {
        '&:hover': {
            color: 'white',
            cursor: 'pointer'
        }
    },
    highlighted: {
        backgroundColor: `${theme.palette.secondary.main}26`
    }
});

export default DrawnSearchAreaInput;
